import { useEffect, useState } from 'react'
import {
  getMaintenanceEndTime,
  getTimeRemaining,
  formatTimeRemaining,
  formatEndTimeLocal,
  formatEndDateLocal,
  type TimeRemaining,
} from '@/lib/maintenance'

/**
 * Full-screen overlay shown during scheduled maintenance
 * Styled to match the XAVIOR landing page design system
 */
export function MaintenanceOverlay() {
  const endTime = getMaintenanceEndTime()
  const [remaining, setRemaining] = useState<TimeRemaining>(() =>
    endTime ? getTimeRemaining(endTime) : { hours: 0, minutes: 0, seconds: 0, totalMs: 0, isExpired: true }
  )

  useEffect(() => {
    if (!endTime) return

    const interval = setInterval(() => {
      const newRemaining = getTimeRemaining(endTime)
      setRemaining(newRemaining)

      // Auto-reload when maintenance ends
      if (newRemaining.isExpired) {
        clearInterval(interval)
        window.location.reload()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [endTime])

  if (!endTime) {
    return null
  }

  const endDateStr = formatEndDateLocal(endTime)
  const endTimeStr = formatEndTimeLocal(endTime)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-surface)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      }}
    >
      {/* Nav-style header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--color-border)',
          background: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 700,
            }}
          >
            X
          </span>
          <span style={{ fontSize: '24px', fontWeight: 700, color: 'var(--color-text-heading)' }}>
            XAVIOR
          </span>
        </div>
      </div>

      {/* Main content - hero style */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div style={{ maxWidth: '600px', textAlign: 'center' }}>
          {/* Maintenance badge */}
          <div
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              background: 'rgba(var(--color-warning-rgb), 0.08)',
              borderRadius: '50px',
              marginBottom: '24px',
            }}
          >
            <span
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--color-warning)',
              }}
            >
              Scheduled Maintenance
            </span>
          </div>

          {/* Title */}
          <h1
            style={{
              fontSize: 'clamp(36px, 8vw, 56px)',
              fontWeight: 800,
              lineHeight: 1.1,
              marginBottom: '20px',
              color: 'var(--color-text-heading)',
            }}
          >
            We'll be right back.
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontSize: 'clamp(16px, 3vw, 20px)',
              color: 'var(--color-text-muted)',
              marginBottom: '48px',
              lineHeight: 1.6,
            }}
          >
            We're making improvements to serve you better. Thanks for your patience.
          </p>

          {/* Countdown card */}
          <div
            style={{
              background: 'var(--color-surface-muted)',
              border: '2px solid var(--color-border)',
              borderRadius: '16px',
              padding: '32px',
              marginBottom: '32px',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '16px',
              }}
            >
              Returning in
            </p>
            <div
              style={{
                fontSize: 'clamp(32px, 10vw, 48px)',
                fontWeight: 800,
                fontFamily: "'Monaco', 'Courier New', monospace",
                color: 'var(--color-primary)',
                letterSpacing: '-0.02em',
              }}
            >
              {formatTimeRemaining(remaining)}
            </div>
          </div>

          {/* End time */}
          <p style={{ fontSize: '18px', color: 'var(--color-text-muted)' }}>
            We'll be back at{' '}
            <span style={{ fontWeight: 600, color: 'var(--color-text-heading)' }}>
              {endTimeStr}
            </span>
            {endDateStr && (
              <span style={{ color: 'var(--color-text-faint)' }}> ({endDateStr})</span>
            )}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '24px',
          textAlign: 'center',
          borderTop: '1px solid var(--color-border)',
        }}
      >
        <p style={{ fontSize: '14px', color: 'var(--color-text-faint)' }}>
          Questions?{' '}
          <a
            href="mailto:support@xavior.ai"
            style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
          >
            support@xavior.ai
          </a>
        </p>
      </div>
    </div>
  )
}
